import { useState, useEffect, useRef } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { Btn, Card, Spinner, ErrorMsg, Badge, useToast } from '../components/ui';
import { intakeProgressSummary } from '../utils/documentIntakeStatus';

// ============================================================================
// ReorganizeDocuments — Settings → "Documents" tab (Admin).
//
// Runs the document intake pipeline over the EXISTING book. A new upload goes
// through it automatically (OCR → Claude reads it → Claude files it from that
// summary and the deal's timeline → SharePoint). Everything uploaded before
// that pipeline existed never has: no summary, and a folder chosen — if at all
// — from the file name. This is how those catch up.
//
// The work itself happens on a QUEUE, in the background. This page only queues
// it: each document then reports its own progress on the Documents screen, the
// same place a fresh upload does, so nobody has to sit here watching. The
// per-deal loop with bounded concurrency is just to keep each request small.
//
// Three scopes, because the second two move real files:
//   • Read only — summarizes documents nobody has read. Nothing is re-filed and
//     no file moves. Safe to run over everything, and the default.
//   • Unfiled — reads and files what is sitting at a deal root. Nothing that
//     already has a folder is touched.
//   • Re-file everything — re-decides the folder for documents that already
//     have one and MOVES them in SharePoint. This reorganises the whole book.
// ============================================================================

const SCOPES = [
  { key: 'summary', label: 'Read documents nobody has read yet',
    hint: 'Claude summarizes each one so it becomes searchable by content. No file is moved and no folder changes — the safe option.' },
  { key: 'unfiled', label: 'Read and file documents with no folder',
    hint: 'Files what is sitting at a deal root, from the summary and where it falls in the deal\'s timeline. Nothing already foldered is moved.' },
  { key: 'sort',    label: 'File the documents Claude has never filed',
    hint: 'The documents read by a summary pass, which stops before choosing a folder. They are NOT sitting at a deal root — they have a folder from the upload picker, the migration, or auto-filing; Claude has just never had an opinion on it. This re-decides only those, and leaves alone both the folders a person chose by hand and the ones Claude already filed. Same cost per document as re-filing, on a fraction of the book.' },
  { key: 'refile',  label: 'Re-file every document',
    hint: 'Re-decides the folder for documents that already have one and moves them in SharePoint. Reorganises the whole book — uses the most AI credits.' },
  { key: 'plan',    label: 'Design each deal’s structure from scratch, then file it',
    hint: 'One Claude call per DEAL instead of one per document: it reads the deal’s documents together, designs the folder structure that fits them, and files every document into it. It is not shown the folders the deal already has, so it designs for the documents rather than inheriting a tree that grew ad hoc — this is the option that genuinely reorganises the book, and it will rename folders that mean the same thing. Far fewer AI credits than re-filing, and it sees the whole deal at once, so it separates diligence-era and operating-era copies of the same document. Only confident placements move; anything borderline keeps the folder it has.' },
];
const CONCURRENCY = 3;

// Both counting reads aggregate the ENTIRE Documents table, so they are slower
// than an ordinary fetch and the client's 10s default was cutting them off — the
// page showed two "Request timed out after 10s" toasts and no numbers at all.
// The queries themselves were fixed (they no longer trim an NVARCHAR(MAX) column
// to test it for emptiness, which was pulling every summary blob off-row), but a
// whole-book aggregate deserves headroom over a per-row read regardless.
const COUNT_TIMEOUT_MS = 60000;

// ─── Live progress across the whole book ─────────────────────────────────────
//
// The work happens on a queue, so the only honest way to know where it has got
// to is to ask the server. This panel is that question answered once, instead
// of five counts to add up by hand — and the counts do NOT add up: a document
// re-queued after an earlier pass keeps its old summary status while also being
// pending, so only "still working" reaching zero means finished.
// ─── Live progress across the whole book ─────────────────────────────────────
//
// THREE pipelines, scored separately. A single combined set of buckets could
// not tell the truth about a re-run: with "in progress" taking precedence over
// everything else, re-queueing the book moved thousands of documents that HAVE
// summaries into the working column and the panel read as though 6,000
// summaries had evaporated. Each stage is now counted on its own state,
// independent of the queue, and each row adds up on its own.
const PIPELINE_COLOURS = {
  done: B.green, unreadable: B.textMd, userChose: B.textMd, pending: B.textMd,
  failed: B.red, notStarted: B.amber, notUploaded: B.amber,
};

// "4 minutes ago" — a bare UTC timestamp in a status line makes the reader do
// arithmetic to answer the only question they have, which is whether it is
// recent.
const fmtAgo = (iso) => {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return 'just now';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1)  return 'less than a minute ago';
  if (mins === 1) return 'a minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? 'an hour ago' : `${hrs} hours ago`;
};

const Chip = ({ label, value, tone }) => (
  <div style={{ minWidth: 92 }}>
    <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2,
                  color: value ? (PIPELINE_COLOURS[tone] || B.text) : B.textLt }}>
      {Number(value || 0).toLocaleString()}
    </div>
    <div style={{ fontSize: 10.5, color: B.textLt, letterSpacing: 0.2 }}>{label}</div>
  </div>
);

// What is moving through ONE stage right now.
//
// The bucket chips above say where the whole book stands; this says what this
// stage is actually doing — how many are queued for it, how many are in flight,
// how fast it finished work in the last hour, and therefore how long ITS queue
// takes. The three stages run at completely different speeds, so a single
// blended rate and ETA (which is what the header used to show) described none
// of them.
const StageFlow = ({ f }) => {
  if (!f) return null;
  // Nothing queued, nothing blocked, nothing failed — the stage is idle and
  // there is nothing worth saying about it.
  if (!f.waiting && !f.blocked && !f.failed) return null;
  const bit = (label, value, color) => (
    <span style={{ color: color || B.textLt }}>
      <strong style={{ color: color || B.textMd, fontVariantNumeric: 'tabular-nums' }}>
        {Number(value).toLocaleString()}
      </strong>{' '}{label}
    </span>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 11, marginTop: 8,
                  color: B.textLt, alignItems: 'baseline' }}>
      {f.queued > 0 && bit(`queued ${f.noun.replace('to ', 'to be ')}`, f.queued)}
      {f.running > 0 && bit('in flight', f.running, B.tealDk)}
      {/* SharePoint only: 'pending' rows whose intake has not finished. They
          are not queued for Graph — the reconciler will not place a document
          until its folder is settled — so showing them as this stage's backlog
          blames Graph for a reading backlog. */}
      {f.blocked > 0 && bit('waiting on an earlier stage', f.blocked, B.amber)}
      {f.failed > 0 && bit('failed', f.failed, B.red)}
      {f.rateLabel && <span>· {f.rateLabel}</span>}
      {f.eta && <span>· {f.eta}</span>}
      {/* Owed work and nothing finishing is the shape of a stuck stage, and it
          is the one thing these numbers say that the bucket counts cannot. */}
      {f.stalled && <span style={{ color: B.amber, fontWeight: 700 }}>· nothing finished this hour</span>}
    </div>
  );
};

const PipelineRow = ({ p, total, flow }) => (
  <div style={{ padding: '12px 0', borderTop: `1px solid ${B.divider}` }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: B.text, minWidth: 130 }}>{p.title}</div>
      <div style={{ fontSize: 12, color: p.complete ? B.green : B.textMd }}>
        {p.done.toLocaleString()} of {total.toLocaleString()}
      </div>
      <div style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700,
                    color: p.complete ? B.green : B.textMd }}>{p.percent}%</div>
    </div>
    <div style={{ height: 6, borderRadius: 3, background: B.border, overflow: 'hidden', marginBottom: 8 }}>
      <div style={{ width: `${p.percent}%`, height: '100%',
                    background: p.complete ? B.green : B.navy, transition: 'width .4s' }} />
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
      {p.parts.map(part => <Chip key={part.key} label={part.label} value={part.value} tone={part.key} />)}
    </div>
    <StageFlow f={flow} />
    {!p.reconciles && (
      <div style={{ fontSize: 11, color: B.red, marginTop: 6 }}>
        These add to {p.bucketed.toLocaleString()}, not {total.toLocaleString()} — treat them as approximate.
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// ReclaimSupportedPanel — documents refused for a file type we can now read.
//
// WIDENING THE READABLE TYPE SET REACHES NOTHING ON ITS OWN, and that is the
// whole reason this panel exists. A document refused for its type keeps the
// refusal in OcrError and is settled 'unsupported', which the summary backfill
// deliberately excludes — re-queueing the genuinely unreadable was half the
// pending pool. So the exclusion that correctly stops us retrying a .dll
// forever also strands anything a later release made readable.
//
// SUPPORTED_OCR_TYPES listed 'heif' and not 'heic'. Apple writes .heic and
// essentially nothing writes .heif, so every phone photo dropped into a deal
// was refused without a call ever being made. Adding the spelling fixed every
// future upload and not one of the 78 already in the book; this is that half.
//
// It renders NOTHING when there is nothing to reclaim, so after a run it
// disappears rather than sitting there offering a no-op. The GET is a dry run —
// it counts without touching anything — so mounting this costs a query.
// ---------------------------------------------------------------------------
const ReclaimSupportedPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    // Best-effort: on a deployment without the endpoint this panel simply never
    // appears, which is the same thing it does when there is nothing to reclaim.
    try { setData(await api.get('documents/ocr/reclaim-supported', { timeout: COUNT_TIMEOUT_MS })); }
    catch { setData(null); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post('documents/ocr/reclaim-supported', {}, { timeout: 300000 });
      const n = res?.requeued || 0;
      if (!n) toast.info('Nothing to reclaim.');
      else toast.success(`Re-queued ${n.toLocaleString()} document${n === 1 ? '' : 's'} for reading.`);
      if (res?.truncated) toast.info('Capped this pass — run it again for the rest.');
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not reclaim those documents');
    } finally { setBusy(false); }
  };

  if (!data || !data.found) return null;   // nothing to reclaim — say nothing

  // The client camel-cases responses (toCamel in @harvus/shared), so a SQL
  // PascalCase column arrives lower-cased. Reading FileType here would have
  // rendered an empty chip list with no error to show for it.
  const types = [...new Set((data.rows || [])
    .map(r => String(r.fileType ?? r.FileType ?? '').toLowerCase()).filter(Boolean))];

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>Now-readable file types</div>
        <div style={{ fontSize: 12, color: B.textLt }}>{data.found.toLocaleString()} documents</div>
      </div>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
        These were refused for a file type the app could not read at the time
        {types.length ? <> — {types.map(t => <code key={t} style={{
          background: B.bgAlt || '#f4f4f5', padding: '1px 5px', borderRadius: 4, marginRight: 4,
        }}>.{t}</code>)}</> : null}
        {' '}and it can read now. Nothing else will pick them up: a type refusal is
        settled permanently, so the background sweep skips them by design. Reclaiming
        reads each one again — no file is moved and no folder changes.
      </div>
      <Btn size="sm" disabled={busy} onClick={run}>
        {busy ? 'Reclaiming…' : `Reclaim ${data.found.toLocaleString()} document${data.found === 1 ? '' : 's'}`}
      </Btn>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// FailedRunsPanel — the documents nothing else will ever pick up again.
//
// The sweep re-queues 'pending' and 'running'; a document it marks 'error' is
// out of the pipeline for good. That is the right default, but it means one bad
// afternoon leaves a pile nothing reclaims — the give-up pass failed over a
// thousand documents for merely waiting their turn in a backlog eighteen hours
// deep, and every count on this page then read as though the work were done.
//
// Grouped by the error, and retryable per group on purpose: a good share of
// these ("Content-Range header is missing or malformed", a name SharePoint will
// not accept) will fail again in exactly the same way, and re-queueing those
// costs a delivery each. Retrying the transient pile without paying for the
// permanent one is the whole point of showing the breakdown.
// ---------------------------------------------------------------------------
//
// PARAMETERISED, because the SharePoint half of the pipeline has exactly the
// same problem and exactly the same shape. Intake failures and placement
// failures are separate columns, separate reconcilers and separate ways back —
// but "here is the pile, grouped by what it actually says, retry a group" is
// one screen, and writing it twice is how the two drift.
const FailedGroupsPanel = ({ title, blurb, listPath, retryPath, retryLabel, resetKey = 'reset' }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [busy, setBusy] = useState(null);   // null | 'all' | the group message

  const load = async () => {
    try {
      setData(await api.get(listPath, { timeout: COUNT_TIMEOUT_MS }));
      setErr(null);
    } catch (e) { setErr(e.message || 'Could not read the failed documents'); }
  };
  useEffect(() => { load(); }, []);

  const retry = async (errorPrefix) => {
    setBusy(errorPrefix || 'all');
    try {
      const res = await api.post(retryPath,
        errorPrefix ? { errorPrefix } : {}, { timeout: 300000 });
      // `reset` is the number that matters: with no storage connection nothing
      // is queued, but the rows are off 'error' and the sweep can see them.
      const n = res?.[resetKey] || 0;
      if (!n) toast.info(res?.message || 'Nothing matched.');
      else if (res?.storage) toast.success(`Reset and queued ${n.toLocaleString()} document${n === 1 ? '' : 's'}.`);
      else toast.success(`Reset ${n.toLocaleString()} document${n === 1 ? '' : 's'}. ${res.message || ''}`);
      if (res?.truncated) toast.info(`Capped at ${res.total.toLocaleString()} this pass — run it again for the rest.`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not retry the failed documents');
    } finally { setBusy(null); }
  };

  if (err) return <Card style={{ marginBottom: 20 }}><ErrorMsg msg={err} /></Card>;
  if (!data || !data.available || !data.total) return null;   // nothing failed — say nothing

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>{title}</div>
        <div style={{ fontSize: 12, color: B.textLt }}>{data.total.toLocaleString()} documents</div>
      </div>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>{blurb}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {data.groups.map(g => (
          <div key={g.message} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
            borderRadius: 6, border: `1px solid ${B.border}`,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: B.text, wordBreak: 'break-word' }}>{g.message}</div>
              <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>
                {g.documents.toLocaleString()} document{g.documents === 1 ? '' : 's'}
                {g.exampleFileName ? ` · e.g. ${g.exampleFileName}` : ''}
              </div>
            </div>
            <Btn size="sm" variant="secondary" disabled={!!busy}
                 onClick={() => retry(g.message)}>
              {busy === g.message ? 'Retrying…' : 'Retry'}
            </Btn>
          </div>
        ))}
      </div>

      <Btn size="sm" disabled={!!busy} onClick={() => retry(null)}>
        {busy === 'all' ? 'Retrying…' : `${retryLabel} all ${data.total.toLocaleString()}`}
      </Btn>
      {data.limit && data.total >= data.limit && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>
          Capped at {data.limit.toLocaleString()} per pass — run it again for the rest.
        </div>
      )}
    </Card>
  );
};

const FailedRunsPanel = () => (
  <FailedGroupsPanel
    title="Failed runs"
    listPath="documents/intake/failed"
    retryPath="documents/intake/retry-failed"
    retryLabel="Retry"
    blurb={'These stopped with an error and nothing picks them up again — the background sweep only '
         + 'retries documents that are queued or running. Each keeps the scope it was queued under, so '
         + 'retrying a read-only pass will not move any files.'}
  />
);

// The SharePoint half. The reconciler deliberately retries only 'pending', so a
// placement Graph refused on its own terms is out of the loop for good — and
// until this panel existed the only way back was one document at a time, on ids
// nobody had. Grouped for the same reason as above: a filename SharePoint could
// not address and a zero-byte file are now fixed and worth retrying in bulk, a
// file with no bytes in it will fail again identically and is not.
const SharePointFailuresPanel = () => (
  <FailedGroupsPanel
    title="Files not in SharePoint"
    listPath="documents/sharepoint/failed"
    retryPath="documents/sharepoint/retry-failed"
    retryLabel="Retry"
    blurb={'These were read and filed in Harvus — the folder is decided — but the upload to SharePoint '
         + 'was refused and nothing retries it. Retrying puts them back in the queue the placement job '
         + 'works from; nothing about the folder is re-decided, so no document changes where it belongs.'}
  />
);


// ---------------------------------------------------------------------------
// SharePointSyncPanel — the OTHER direction, and the one nothing could see.
//
// Every panel above is about documents Harvus already has. This one is about
// files it does not: something dropped straight into Deals/{Deal}/… through
// OneDrive or the SharePoint web UI. A timer walks the drive's changes and
// imports them — and when it silently imports nothing, the Documents list is
// indistinguishable from a drive where nothing was added. That is how the whole
// inbound path managed to be dead for the life of the feature.
//
// TWO NUMBERS ANSWER THE QUESTION PEOPLE ACTUALLY ASK ("I put a file in
// SharePoint, where is it?"): when the drive was last read END TO END, and what
// that read could not place, by reason. A pass that ran five minutes ago says
// nothing about coverage — an incremental pass reports "complete" having looked
// at nothing at all.
// ---------------------------------------------------------------------------
const SKIP_WORDS = {
  // `error` first because it is the one that means something is broken rather
  // than merely unmatched — it is a file the pass TRIED to import and could not.
  error:      'failed while being imported — this is the one to read first',
  'no-deal':  'under Deals/ but the folder name matches no deal — create or rename the deal and they come in',
  unplaced:   'the folder could not be resolved — usually a Graph error, and the next full read retries them',
  outside:    'not under Deals/, so there is no deal to attach them to. Correct, not a problem',
  'too-big':  'over the per-file size cap',
};

const ago = (iso) => {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins)) return null;
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

// ---------------------------------------------------------------------------
// ASK ABOUT ONE DEAL.
//
// The panel above describes a walk of the whole drive, and every way that walk
// can fail to place a file reads the same from outside — complete, nothing
// imported, no error. This asks Graph about the one folder somebody is actually
// looking at, compares it against what Harvus holds, and imports the
// difference. Idempotent, so it is safe to press twice.
// ---------------------------------------------------------------------------
const VERDICT_TONE = {
  'no-folder': B.red,
  empty:       B.red,
  refused:     B.red,
  imported:    B.tealDk,
  'would-import': B.navy,
  // Amber, not red: the folder is not broken and nothing was refused — the read
  // simply did not finish, and what it DID read is still an answer.
  partial:     B.amber,
  'all-held':  B.textMd,
};

// A run id the SERVER did not mint, because the client has to be able to name a
// run whose response it never saw. `crypto.randomUUID` is there over HTTPS; the
// fallback only has to be unique enough to tell two presses apart.
const newRunId = () => {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* fall through */ }
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Reads the row the run wrote for itself, and only ever trusts THIS run's.
// A row from an earlier press reported as this one's outcome would be the worse
// failure: a stale "all held" over a scan that actually fell over.
const pollLastRun = async (runId, tries = 5) => {
  for (let i = 0; i < tries; i++) {
    try {
      const d = await api.get('sharepoint/deal-scan/last');
      if (d?.available && d.run?.runId === runId) {
        if (d.run.status !== 'running' || i === tries - 1) return d.run;
      }
    } catch { /* the row is a best-effort diagnostic; keep trying */ }
    await new Promise(r => setTimeout(r, 3000));
  }
  return null;
};

const DealSharePointScan = () => {
  const { toast } = useToast();
  const [deals, setDeals] = useState(null);
  const [dealId, setDealId] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    let off = false;
    // camelCase, NOT the SQL PascalCase the route returns: the shared client
    // lower-cases the first character of every response key (`toCamel` in
    // `shared/src/apiCore.ts`), so `d.DealID` here is undefined and every option
    // renders blank with an empty value — a picker that looks loaded and can
    // never be chosen from. `useDealPickerItems` reads the same two fields.
    api.get('deal-detail-options')
      .then(d => { if (!off) setDeals((d?.deals || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))); })
      .catch(e => { if (!off) setErr(e.message); });
    return () => { off = true; };
  }, []);

  const run = async () => {
    if (!dealId) return;
    // THE CLIENT MINTS THE RUN ID, so it can ask what happened to the very
    // request whose response it never received. Past ~45 s the Static Web Apps
    // edge closes the request and answers with an opaque bodyless 500 while the
    // scan carries on server-side — which is indistinguishable, from here, from
    // the scan never having run. The run records itself either side of the work,
    // so the row is the outcome and this request's own error is only worth
    // showing when that row says the work never began (`SageImport.jsx`'s rule).
    const runId = newRunId();
    setBusy(true); setRes(null); setErr(null); setNote(null);
    try {
      // The server walks the deal's folder over Graph and imports what is
      // missing, so this outlasts the client's 10 s default comfortably.
      const out = await api.post('sharepoint/deal-scan',
        { dealId, runId, previewOnly: preview }, { timeout: 120000 });
      setRes(out);
      if (out?.imported > 0) toast.success(out.sentence);
    } catch (e) {
      const row = await pollLastRun(runId);
      if (row && (row.status === 'done' || row.status === 'error')) {
        setRes(row.result || null);
        setNote('The request didn\u2019t make it back \u2014 the gateway closed it \u2014 '
              + 'but the scan finished on the server. This is what it did.');
      } else if (row) {
        // It started and has not recorded an outcome: still going, or the
        // worker died part-way. Either way pressing again is safe.
        setNote('The scan started and hasn\u2019t finished yet. Give it a moment and press '
              + 'again \u2014 anything already imported is kept and is not imported twice.');
      } else {
        setErr(e.message); toast.error(e.message);
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: B.text, marginBottom: 4 }}>
        Look for a file on one deal
      </div>
      <div style={{ fontSize: 12.5, color: B.textMd, lineHeight: 1.5, marginBottom: 10 }}>
        Reads that deal&rsquo;s SharePoint folder directly and imports anything Harvus does not
        already hold. It doesn&rsquo;t wait for the drive-wide read above, and it says which of the
        four things is true &mdash; no folder, an empty folder, everything already here, or files
        that were missing. On a folder too large to read in one press, pressing it again continues
        from where it stopped rather than starting over. Safe to press twice.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={dealId}
          onChange={e => { setDealId(e.target.value); setRes(null); setErr(null); setNote(null); }}
          disabled={!deals || busy}
          style={{ padding: '7px 10px', fontSize: 13, minWidth: 260,
                   border: `1px solid ${B.border}`, borderRadius: 8, background: '#fff', color: B.text }}
        >
          <option value="">{deals ? 'Choose a deal…' : 'Loading deals…'}</option>
          {(deals || []).map(d => <option key={d.dealID} value={d.dealID}>{d.name}</option>)}
        </select>
        <Btn variant="secondary" onClick={run} disabled={!dealId || busy}>
          {busy ? 'Reading SharePoint…' : preview ? 'List what is missing' : 'Find missing files'}
        </Btn>
        {/* PREVIEW IS THE BISECT, NOT A CONVENIENCE. A preview reads the folder
            and compares it against dbo.Documents — no download, no blob write,
            no import — so it costs the two Graph calls and nothing else. If a
            preview answers and a real scan does not, the time is going into the
            import loop; if a preview fails the same way, it is the folder walk
            before it. Those are the two halves the bare 500 could not tell
            apart. The endpoint has always accepted `previewOnly`; nothing sent it. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: B.textMd }}>
          <input type="checkbox" checked={preview} disabled={busy}
                 onChange={e => { setPreview(e.target.checked); setRes(null); setErr(null); setNote(null); }} />
          Just list them (don&rsquo;t import)
        </label>
      </div>

      {err && <div style={{ marginTop: 10 }}><ErrorMsg msg={err} /></div>}

      {note && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: B.textMd, lineHeight: 1.5 }}>
          {note}
        </div>
      )}

      {res && (
        <div style={{ marginTop: 12 }}>
          {/* The server's own sentence, verbatim — the client does not restate
              a verdict it did not compute. */}
          <div style={{ fontSize: 13, fontWeight: 600,
                        color: VERDICT_TONE[res.verdict] || B.text }}>
            {res.sentence || (res.available === false ? `Not available — ${res.reason}.` : null)}
          </div>
          {/* A FAILURE NAMES ITS STEP. The server answers 200 with the reason on
              it rather than a bare 500 (whose detail production strips), so the
              one thing this panel must never do is throw that away. */}
          {res.available === false && res.step && (
            <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>
              Failed while {res.step}
              {res.rawError && <> — <code>{res.rawError}</code></>}
            </div>
          )}
          {res.available !== false && (
            <div style={{ fontSize: 12, color: B.textLt, marginTop: 4 }}>
              <code>{res.folderPath}</code> · {res.fileCount} file{res.fileCount === 1 ? '' : 's'} in SharePoint
              {res.alreadyHeld > 0 && <> · {res.alreadyHeld} already in Harvus</>}
              {res.truncated && <> · more than this pass could read</>}
            </div>
          )}
          {/* A folder Graph refused to read is named rather than silently
              dropped — one bad sub-folder no longer fails the whole scan, but
              it also must not read as though everything under it was checked. */}
          {(res.folderErrors || []).length > 0 && (
            <div style={{ fontSize: 12, color: B.amber, marginTop: 4 }}>
              {res.folderErrors.length} sub-folder{res.folderErrors.length === 1 ? '' : 's'} could not be
              read and {res.folderErrors.length === 1 ? 'was' : 'were'} skipped:{' '}
              {res.folderErrors.map(e => e.relPath).join(', ')}
            </div>
          )}
          {/* Anything that did not land is NAMED. A count with no reason is the
              dead end this whole feature kept arriving at. */}
          {(res.files || []).filter(f => f.state !== 'already-tracked' && f.state !== 'duplicate').length > 0 && (
            <div style={{ marginTop: 8 }}>
              {(res.files || [])
                .filter(f => f.state !== 'already-tracked' && f.state !== 'duplicate')
                .slice(0, 25)
                .map((f, i) => (
                  <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5,
                                        color: f.state === 'imported' ? B.textMd
                                             : f.state === 'would-import' ? B.textMd : B.red }}>
                    {f.state === 'imported' ? '✓' : f.state === 'would-import' ? '·' : '×'} {f.name}
                    {f.state === 'would-import' && <span style={{ color: B.textLt }}> — not in Harvus</span>}
                    {f.reason && <span style={{ color: B.textLt }}> — {f.reason}</span>}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SharePointSyncPanel = () => {
  const { toast } = useToast();
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);
  const [busy, setBusy]   = useState(null);
  const [last, setLast]   = useState(null);   // the pass this page just ran

  const load = async () => {
    try { setData(await api.get('sharepoint/delta/status')); setErr(null); }
    catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);

  const run = async (resync) => {
    setBusy(resync ? 'resync' : 'scan');
    setLast(null);
    try {
      // A full re-read walks the whole drive, so it needs far longer than the
      // client's default — the SageImport.jsx lesson: a timeout reported over a
      // pass that actually ran is the same failure one screen over.
      const out = await api.post('sharepoint/delta/scan',
        { resync: resync === true, budgetMs: resync ? 240000 : 120000 },
        { timeout: 300000 });
      setLast(out);
      if (out.imported) toast.success(`Imported ${out.imported} file${out.imported === 1 ? '' : 's'} from SharePoint.`);
      else if (out.skippedRun) toast.info('A scan is already running — it will publish its own result.');
      else toast.success('Read SharePoint. Nothing new to import.');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  if (err) return <Card style={{ marginBottom: 20 }}><ErrorMsg msg={err} /></Card>;
  if (!data) return null;

  if (data.available === false) {
    return (
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Files added in SharePoint</div>
        <div style={{ fontSize: 13, color: B.textMd }}>Not running — {data.reason}.</div>
      </Card>
    );
  }

  const skips   = data.unplaced || {};
  const kinds   = Object.keys(skips).filter(k => skips[k]?.count > 0);
  // The headline comes from the SERVER (`outside` is a correct outcome and stays
  // out of it; `error` is urgent and stays in). Re-deriving it here would be a
  // second copy of a rule that has already changed once.
  const actionable = Number(data.unplacedActionable) || 0;

  const walked = data.lastFullWalkUTC;
  const stale  = data.tracksFullWalk && !walked;
  const seen   = Number(data.itemsSeen) || 0;
  const finishedNothing = data.tracksFullWalk && walked && data.fullWalkComplete === false;

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Files added in SharePoint</div>
          <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.5 }}>
            A file dropped straight into <code>Deals/{'{Deal}'}/…</code> — through OneDrive or the
            SharePoint web UI — is picked up automatically and appears on that deal. This is that half
            of the sync, and the only place it is visible.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={() => run(false)} disabled={!!busy}>
            {busy === 'scan' ? 'Checking…' : 'Check now'}
          </Btn>
          <Btn variant="secondary" onClick={() => run(true)} disabled={!!busy}>
            {busy === 'resync' ? 'Reading…' : 'Re-read everything'}
          </Btn>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 14,
                    fontSize: 12.5, color: B.textMd }}>
        <div>
          <div style={{ color: B.textLt }}>Last checked for changes</div>
          <div style={{ fontWeight: 600, color: B.text }}>{ago(data.lastRunUTC) || 'never'}</div>
        </div>
        {data.tracksFullWalk && (
          <div>
            {/* THE STAMP IS WRITTEN WHEN THE WALK BEGINS, so "1 min ago" under a
                heading reading "end to end" claims coverage the pass may never
                have had — a re-read is bounded on pages, imports and wall clock,
                and stopping after one page is ordinary on a large drive. The
                server says which; this only renders it. */}
            <div style={{ color: B.textLt }}>
              {data.fullWalkComplete ? 'Drive last read end to end' : 'Full re-read started'}
            </div>
            <div style={{ fontWeight: 600, color: (stale || finishedNothing) ? B.red : B.text }}>
              {ago(walked) || 'not yet'}
              {walked && !data.fullWalkComplete && (
                <span style={{ color: B.red, fontWeight: 500 }}> — did not reach the end</span>
              )}
            </div>
          </div>
        )}
        {/* THE NUMBER THAT SEPARATES "the walk cannot see this drive" FROM "it
            sees it and places nothing". Without it, imported: 0 reads the same
            either way, and those want opposite fixes. */}
        <div>
          <div style={{ color: B.textLt }}>Items looked at, to date</div>
          <div style={{ fontWeight: 600, color: seen === 0 ? B.red : B.text }}>{seen}</div>
        </div>
        <div>
          <div style={{ color: B.textLt }}>Imported to date</div>
          <div style={{ fontWeight: 600, color: B.text }}>{data.itemsImported ?? 0}</div>
        </div>
        {data.scanInFlight && <Badge color={B.tealDk}>a read is running now</Badge>}
        {data.enabled === false && <Badge color={B.red}>paused</Badge>}
      </div>

      {data.lastError && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: B.red }}>
          Last error: {data.lastError}
        </div>
      )}

      {/* THE LIST THAT MAKES A SILENT PASS READABLE. Until this existed, a pass
          that walked the whole drive and placed none of it reported exactly
          what a healthy one does. */}
      {data.tracksFullWalk && kinds.length > 0 && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${B.border}`, paddingTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: B.text, marginBottom: 6 }}>
            In SharePoint, not in Harvus
            {actionable > 0 && <span style={{ color: B.red }}> — {actionable} worth looking at</span>}
          </div>
          {kinds.map(k => (
            <div key={k} style={{ fontSize: 12.5, color: B.textMd, marginBottom: 5, lineHeight: 1.45 }}>
              <strong style={{ color: B.text }}>{skips[k].count}</strong>{' '}
              {SKIP_WORDS[k] || k}
              {skips[k].example && (
                <div style={{ color: B.textLt, fontSize: 12 }}>e.g. {skips[k].example}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* An empty tally is the healthy answer ONLY over a walk that actually
          read the drive. Over one that looked at nothing it is the absence of
          evidence printed as evidence of absence — which is the whole failure
          this panel was built to stop making. */}
      {data.tracksFullWalk && walked && kinds.length === 0 && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: seen === 0 ? B.red : B.textMd }}>
          {seen === 0
            ? <>The walk has not looked at a single item, so nothing here is evidence the
               drive is clean. Check <code>SHAREPOINT_DRIVE_ID</code> and that the app
               registration can read that library.</>
            : <>Nothing under <code>Deals/</code> was refused.</>}
        </div>
      )}

      {last && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: B.textMd }}>
          That pass{last.resynced ? ' re-read the drive from the top and' : ''} looked at {last.seen} item
          {last.seen === 1 ? '' : 's'}, imported {last.imported}
          {last.errors?.length ? `, and hit ${last.errors.length} error(s)` : ''}.
          {last.reason ? ` ${last.reason}.` : ''}
          {/* NAME THE FAILURE. A count of errors with no text is the same dead
              end as no count at all — the message is the only actionable part. */}
          {last.errors?.length > 0 && (
            <div style={{ marginTop: 6, color: B.red, fontSize: 12, lineHeight: 1.45 }}>
              {last.errors.slice(0, 3).map((e, i) => <div key={i}>{e}</div>)}
              {last.errors.length > 3 && <div>…and {last.errors.length - 3} more.</div>}
            </div>
          )}
        </div>
      )}

      {/* Said in words, because "no subscription" reads like an outage and is
          not one — the timer is the authority and converges regardless. */}
      <div style={{ marginTop: 12, fontSize: 12, color: B.textLt, lineHeight: 1.5 }}>
        Checked every few minutes, and the whole drive is re-read every {data.reseedAfterDays ?? 7} days
        so anything missed earlier is picked up without anyone asking. <strong>Re-read everything</strong> does
        that now; it only imports files Harvus does not already hold, so it is safe to press.
      </div>

      <DealSharePointScan />
    </Card>
  );
};

// ---------------------------------------------------------------------------
// RefilePause — hold the bulk re-file without stopping the pipeline.
//
// Re-filing the book and designing a deal's structure from a plan both decide
// where a document belongs, and they overwrite each other. Before this there
// was no way to stop the first while running the second: the sweep re-queues
// every ten minutes, and a message enqueued moments before a plan ran would
// re-sort the document per file and quietly undo it.
//
// Narrow on purpose — a new upload is still read and filed, and a genuinely
// stuck document is still rescued. Only the bulk re-decision pauses.
// ---------------------------------------------------------------------------
const RefilePause = ({ data }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);   // optimistic, until the next poll
  const enabled = local !== null ? local : data?.refileEnabled !== false;

  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      const res = await api.post('documents/intake/refile-enabled', { enabled: next },
        { timeout: COUNT_TIMEOUT_MS });
      setLocal(res?.enabled !== false);
      if (res?.saved === false) toast.error(res.message || 'The setting could not be saved.');
      else toast.success(next
        ? 'Re-filing resumed.'
        : 'Re-filing paused. Documents already in the queue stop within a few seconds.');
    } catch (e) {
      toast.error(e.message || 'Could not change the setting');
    } finally { setBusy(false); }
  };

  if (!data?.available) return null;
  return (
    <Btn size="sm" variant="secondary" disabled={busy} onClick={toggle}>
      {busy ? 'Saving…' : enabled ? 'Pause re-filing' : 'Resume re-filing'}
    </Btn>
  );
};

const IntakeProgressPanel = ({ onQueueRemaining, queueing }) => {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  // A nonce rather than lifting `load` out of the effect: the poll owns its own
  // timer and cancellation, and an action that changes the answer (draining the
  // poison queue) only needs to say "ask again now".
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const d = await api.get('documents/intake/status', { timeout: COUNT_TIMEOUT_MS });
        if (cancelled) return;
        setData(d); setErr(null);
        // Poll only while something is actually moving; a finished book stops.
        const busy = d?.available && (d.working > 0 || (d.reading?.notStarted || 0) > 0);
        timer = setTimeout(load, busy ? 15000 : 120000);
      } catch (e) {
        if (cancelled) return;
        setErr(e.message || 'Could not read progress');
        timer = setTimeout(load, 60000);
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [nonce]);

  if (err)   return <Card style={{ marginBottom: 20 }}><ErrorMsg msg={err} /></Card>;
  if (!data) return <Card style={{ marginBottom: 20 }}><Spinner label="Reading progress…" /></Card>;
  if (!data.available) {
    return (
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: B.textMd }}>
          Document processing isn't set up on this database yet{data.reason ? ` — ${data.reason}` : ''}.
        </div>
      </Card>
    );
  }

  const s = intakeProgressSummary(data);

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>Document processing</div>
        <div style={{ fontSize: 12, color: B.textLt }}>{s.total.toLocaleString()} documents</div>
        <div style={{ marginLeft: 'auto' }}><RefilePause data={data} /></div>
      </div>

      {/* A pause must never read as a stall. The rate and the "left" estimate
          above keep counting down from work that is no longer being fed. */}
      {data.refileEnabled === false && (
        <div style={{ fontSize: 12, color: B.amber, marginBottom: 6 }}>
          Re-filing is paused — documents that already have a folder are being left alone.
          New uploads are still read and filed.
        </div>
      )}
      <div style={{ fontSize: 13, color: s.stalled ? B.amber : B.textMd, marginBottom: 6 }}>
        {s.label}
        {s.eta && <span style={{ color: B.textLt }}> · {s.eta}</span>}
        {s.rateLabel && <span style={{ color: B.textLt }}> · {s.rateLabel}</span>}
      </div>

      {/* A document in the queue is being RE-read, not un-read: its summary is
          still there. Saying so stops a re-run looking like lost work. */}
      {s.working > 0 && (
        <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>
          {s.working.toLocaleString()} in the queue right now
          {/* WHICH queue. One number across three stages could not say whether
              these documents were waiting on Claude to read them, on Claude to
              file them, or on Graph to upload them. */}
          {s.queueSplit ? ` — ${s.queueSplit}` : ''}.
          {' '}Documents already read keep their summary while they wait.
        </div>
      )}

      {s.pipelines.map(p => <PipelineRow key={p.key} p={p} total={s.total}
                                          flow={(s.flows || []).find(f => f.key === p.key)} />)}

      <div style={{ fontSize: 11, color: B.textLt, marginTop: 10 }}>
        "Can't be read" is finished, not broken — video, archives, fonts and the older .doc/.xls/.ppt formats have no text to extract.
      </div>

      {/* Anything without a summary can be retried, and the backfill's scope
          selects exactly that — so this has to cover FAILED documents as well
          as never-read ones. A failure is very often not about the document at
          all (an exhausted credit balance failed 2,713 in one go), and offering
          a retry only for the never-read half would strand the rest. */}
      {s.retryable > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8,
                      border: `1px solid ${B.amber}55`, background: `${B.amber}0d` }}>
          <div style={{ fontSize: 13, color: B.text, marginBottom: 8 }}>
            <strong>{s.retryable.toLocaleString()}</strong> document{s.retryable === 1 ? '' : 's'} can be read:
            {' '}{s.reading.parts.find(x => x.key === 'notStarted')?.value.toLocaleString()} never read,
            {' '}{s.reading.parts.find(x => x.key === 'failed')?.value.toLocaleString()} failed.
          </div>
          <Btn size="sm" onClick={onQueueRemaining} disabled={queueing}>
            {queueing ? 'Queueing…' : 'Queue them all now'}
          </Btn>
        </div>
      )}

      {!!(s.working && data.remainingByType?.length) && (
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 12 }}>
          In the queue: {data.remainingByType.map(r => `${r.documents.toLocaleString()} ${r.fileType}`).join(' · ')}
        </div>
      )}

      <QueueHealth queue={data.queue} waiting={s.working} finishing={data.finishedLast10Min || 0}
                   sweep={data.lastSweep} onChanged={() => setNonce(n => n + 1)} />
      <DiagnosticsButton />
      <ResetCountersButton onDone={() => setNonce(n => n + 1)} />
    </Card>
  );
};

// One button, one JSON payload, everything about the pipeline.
//
// Every question worth asking during a stuck run — is the queue being fed, what
// are the failures actually saying, is the sweep seeing the backlog, where is
// the time going — used to need ad-hoc SQL and the Azure portal, which meant it
// mostly got guessed at instead. This puts the whole picture one click away, in
// a form that can be pasted straight into a conversation.
// START A CLEAN MEASUREMENT WINDOW.
//
// The worker outcome counters are lifetime totals, so `unexplained` — the
// residual meant to say "runs are ending somewhere nothing counts" — is
// computed across every build that ever ran. A path that failed to count two
// deploys ago is permanently in it and can never drain, and retired counter
// names (done-sharepoint and friends, from before the SharePoint move was split
// out of the run) are still sitting in the store. It read as 3,318 lost
// messages while every exit path in the current worker tallies.
//
// Resetting is the only thing that makes the number diagnostic: after it,
// anything that reappears is real and can be chased in the code that is
// actually running. Nothing about the pipeline changes — these are counters.
const ResetCountersButton = ({ onDone }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    if (!window.confirm(
      'Start a clean counter window?\n\n'
      + 'This clears the worker outcome counters and starts measuring from now. '
      + 'It does not touch any document, any queue, or any file — only the tallies '
      + 'the diagnostics report.\n\n'
      + 'Do this once after a deploy so `unexplained` measures the build that is '
      + 'actually running.')) return;
    setBusy(true);
    try {
      const res = await api.post('documents/intake/counters/reset', {}, { timeout: 120000 });
      const cleared = (res?.cleared || []).reduce((n, c) => n + (c.value || 0), 0);
      toast.success(`Counters reset — ${cleared.toLocaleString()} old counts cleared. Measuring from now.`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.message || 'Could not reset the counters');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${B.divider}`, paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Btn variant="ghost" onClick={reset} disabled={busy} style={{ padding: '6px 12px', fontSize: 12 }}>
          {busy ? 'Resetting…' : '↺ Reset counters'}
        </Btn>
        <span style={{ fontSize: 11.5, color: B.textLt }}>
          The outcome counters are lifetime totals, so they span every deploy — which makes
          &ldquo;unexplained&rdquo; unreadable. Reset once after a release; anything that
          reappears afterwards is real. Touches no document and no file.
        </span>
      </div>
    </div>
  );
};

const DiagnosticsButton = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [dump, setDump] = useState(null);

  const grab = async () => {
    setBusy(true);
    try {
      const d = await api.get('documents/intake/diagnostics', { timeout: COUNT_TIMEOUT_MS });
      const text = JSON.stringify(d, null, 2);
      setDump(text);
      // Clipboard is best-effort: it needs a secure context and a permission
      // that a browser can refuse, so the textarea below is the real delivery
      // and copying is the convenience.
      try { await navigator.clipboard.writeText(text); toast.success('Diagnostics copied to the clipboard.'); }
      catch { toast.success('Diagnostics ready — select the text below and copy.'); }
    } catch (e) {
      toast.error(e.message || 'Could not collect diagnostics');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${B.divider}`, paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Btn onClick={grab} disabled={busy} style={{ padding: '6px 12px', fontSize: 12 }}>
          {busy ? 'Collecting…' : '⛑ Copy diagnostics'}
        </Btn>
        <span style={{ fontSize: 11.5, color: B.textLt }}>
          Config, queue depth, what is pending, every error grouped by message, and measured
          throughput — as JSON, for handing to whoever is debugging it.
        </span>
        {dump && (
          <button onClick={() => setDump(null)}
            style={{ marginLeft: 'auto', fontSize: 11, background: 'none', border: 'none',
                     color: B.textLt, cursor: 'pointer', textDecoration: 'underline' }}>
            hide
          </button>
        )}
      </div>
      {dump && (
        <textarea readOnly value={dump} onFocus={e => e.target.select()}
          style={{ width: '100%', height: 260, marginTop: 10, fontSize: 11,
                   fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                   border: `1px solid ${B.border}`, borderRadius: 6, padding: 8,
                   background: B.surface, color: B.text, whiteSpace: 'pre' }} />
      )}
    </div>
  );
};

// Marked pending in the database vs actually sitting on the storage queue.
//
// These two numbers being wildly apart is the difference between "the pipeline
// is slow" and "the pipeline is not being fed", and nothing surfaced it — the
// only way to tell was the Azure portal, so the natural reading of a big
// backlog was that work was queued and moving. It can equally mean thousands of
// rows whose enqueues never landed, left to a ten-minute sweep that falls back
// to running them one at a time. Same screen, completely different fix.
const QueueHealth = ({ queue, waiting, finishing, sweep, onChanged }) => {
  const { toast } = useToast();
  const [draining, setDraining] = useState(false);

  const drain = async () => {
    setDraining(true);
    try {
      const res = await api.post('documents/intake/poison/drain', {}, { timeout: 300000 });
      const n = res?.moved || 0;
      if (!n) toast.info('Nothing was set aside.');
      else toast.success(`Put ${n.toLocaleString()} message${n === 1 ? '' : 's'} back on the queue.`);
      if (res?.failed) toast.info(`${res.failed} could not be moved.`);
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Could not put those messages back');
    } finally { setDraining(false); }
  };

  if (!queue) return null;
  if (!queue.available) {
    return (
      <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>
        Couldn't read the processing queue{queue.reason ? ` — ${queue.reason}` : ''}.
      </div>
    );
  }
  const onQueue = queue.messages;
  const poison  = queue.poison || 0;

  // A NEAR-EMPTY QUEUE IS NOT A FAULT — it is what a healthy fast one looks
  // like. This first compared depth against the database backlog, which made
  // the alarm fire on a pipeline clearing thousands an hour: the sweep hands
  // work over in batches and the workers drain each batch faster than the next
  // arrives, so depth sits near zero precisely when everything is working.
  //
  // The honest test is whether documents are coming out the other end. Nothing
  // queued AND nothing finishing is a stall worth shouting about; nothing
  // queued while documents keep completing is just a queue keeping up.
  const starved = waiting > 200 && onQueue !== null && onQueue < 5 && finishing === 0;

  // WHAT THE SWEEP LAST DID, in a sentence, because "enqueued 0" means three
  // completely different things and only one of them is a problem.
  //
  // The sweep tops the queue UP to a target depth rather than pushing a fixed
  // number into it — offering more than the workers can take just gets the same
  // document delivered repeatedly until the queue gives up on it. So during a
  // healthy backfill it enqueues nothing most ticks, and without this line that
  // is indistinguishable from a broken producer.
  const sweepLine = !sweep ? null
    : sweep.heldBack
      ? `Last check ${fmtAgo(sweep.at)}: the queue already held ${(sweep.queueDepth ?? 0).toLocaleString()} `
        + `documents, so nothing was added. That is the pipeline keeping up, not stalling.`
    : sweep.enqueued || sweep.ranInline
      ? `Last check ${fmtAgo(sweep.at)}: queued ${(sweep.enqueued || 0).toLocaleString()}`
        + (sweep.ranInline ? `, ran ${sweep.ranInline.toLocaleString()} directly` : '')
        + (sweep.cappedByLimit ? ' — capped, so there is more behind it.' : '.')
    : `Last check ${fmtAgo(sweep.at)}: nothing was waiting to be picked back up.`;

  return (
    <div style={{ fontSize: 12, marginTop: 6, color: starved ? B.red : B.textLt }}>
      {onQueue === null ? 'Queue reachable.' : <>On the processing queue: <strong>{onQueue.toLocaleString()}</strong></>}
      {poison > 0 && (
        <> · <span style={{ color: B.red }}>{poison.toLocaleString()} gave up permanently</span></>
      )}
      {sweepLine && <div style={{ marginTop: 4 }}>{sweepLine}</div>}
      {queue.lastEnqueueError && (
        <div style={{ marginTop: 4 }}>
          Last time something tried to queue a document it failed:{' '}
          <strong>{queue.lastEnqueueError.message}</strong>
          {' '}— until that is fixed the ten-minute sweep is the only thing moving work.
        </div>
      )}
      {starved && (
        <div style={{ marginTop: 4 }}>
          {waiting.toLocaleString()} documents are waiting, none are queued, and nothing has
          finished in the last ten minutes — the workers are not being fed. Adding capacity will
          not help until that is fixed.
        </div>
      )}
      {/* THE ONE-CLICK WAY BACK. A message the queue gave up on takes its
          document with it: the row stays 'pending' forever and every count on
          this page carries a document nothing is working on. The endpoint to
          rescue them existed and nothing called it, which made the number above
          a statement of fact with no next step. */}
      {poison > 0 && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>
            Messages that failed repeatedly are set aside and never retried, so those documents will
            stay "waiting" until they are queued again.
          </span>
          <Btn size="sm" variant="secondary" disabled={draining} onClick={drain}>
            {draining ? 'Putting back…' : `Put ${poison.toLocaleString()} back on the queue`}
          </Btn>
        </div>
      )}
    </div>
  );
};

export const ReorganizeDocuments = () => {
  const { toast } = useToast();
  const [scope, setScope]     = useState('summary');
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [phase, setPhase]     = useState('idle');   // idle | running | done
  const [progress, setProgress] = useState({ done: 0, filed: 0, failed: 0, total: 0 });
  const [log, setLog]         = useState([]);       // newest first
  const stopRef = useRef(false);
  const [queueing, setQueueing] = useState(false);

  // Queue EVERY remaining document in one server-side request.
  //
  // The per-deal loop below exists so each request stays small, but its coverage
  // depends on the browser finishing the loop — close the page and the deals it
  // never reached are silently skipped, which is exactly how documents end up
  // never queued. This path hands the whole book to the server in one call, so
  // nothing depends on the page staying open.
  const queueRemaining = async () => {
    setQueueing(true);
    try {
      const res = await api.post('documents/intake/backfill', { scope }, { timeout: 300000 });
      toast.success(`Queued ${(res?.enqueued || 0).toLocaleString()} document${res?.enqueued === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(e.message || 'Could not queue the remaining documents');
    } finally { setQueueing(false); }
  };

  // (Re)load the targets whenever the scope changes, while idle.
  useEffect(() => {
    if (phase !== 'idle') return undefined;
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`documents/intake/backfill/targets?scope=${scope}`, { timeout: COUNT_TIMEOUT_MS })
      .then(d => { if (!cancelled) setTargets(d); })
      .catch(e => { if (!cancelled) setErr(e.message || 'Failed to load deals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, phase]);

  const run = async () => {
    const deals = (targets?.targets || []).slice();
    if (!deals.length) return;
    stopRef.current = false;
    setPhase('running');
    setProgress({ done: 0, filed: 0, failed: 0, total: deals.length });
    setLog([]);

    let idx = 0;
    const worker = async () => {
      // `idx++` is synchronous before any await, so no two workers take the same deal.
      while (!stopRef.current) {
        const deal = deals[idx++];
        if (!deal) return;
        let filed = 0;
        let failed = false;
        try {
          // Enqueue-only, so this returns quickly — the reading and filing
          // happen on the queue afterwards. `filed` here counts documents
          // QUEUED, not documents finished; the Documents screen is where each
          // one's real progress shows up.
          const res = await api.post('documents/intake/backfill', {
            dealId: deal.dealID, scope,
          }, { timeout: 120000 });
          // A PAUSE STOPS THE LOOP, it does not fail 81 deals one at a time.
          // The server refuses each request identically while re-filing is
          // held, so carrying on would just fill the log with the same line.
          if (res?.paused) {
            stopRef.current = true;
            toast.error(res.error || 'Re-filing is paused — resume it first.');
            setLog(l => [{ dealName: deal.dealName, filed: 0, error: res.error }, ...l].slice(0, 200));
            return;
          }
          filed = res?.enqueued || 0;
          // A DEAL CAN FAIL WITHOUT THE REQUEST FAILING. The `plan` scope does
          // the work for one deal inside the request and reports the reason in
          // the body; reading only `failed` counted it as a success with zero
          // documents and threw the message away.
          failed = !!res?.error || ((res?.failed || 0) > 0 && filed === 0);
          setLog(l => [{
            dealName: deal.dealName, filed,
            error: res?.error || null,
            failures: res?.failed || 0,
            skipped: Math.max(0, (res?.total || 0) - (res?.enqueued || 0) - (res?.failed || 0)),
          }, ...l].slice(0, 200));
        } catch (e) {
          failed = true;
          setLog(l => [{ dealName: deal.dealName, error: e.message }, ...l].slice(0, 200));
        }
        setProgress(p => ({
          done: p.done + 1, filed: p.filed + filed,
          failed: p.failed + (failed ? 1 : 0), total: p.total,
        }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, deals.length) }, worker));
    setPhase('done');
    toast.success('Queued. The documents are being processed in the background.');
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div>
      <IntakeProgressPanel onQueueRemaining={queueRemaining} queueing={queueing} />
      <ReclaimSupportedPanel />
      <FailedRunsPanel />
      <SharePointFailuresPanel />
      <SharePointSyncPanel />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Catch up existing documents</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16, lineHeight: 1.5 }}>
          Every new upload is read by Claude, filed by what it says and where it falls in the deal's
          timeline, and put into SharePoint automatically. Documents uploaded before that was true have
          never been read, and were filed — if at all — from their file name. This runs them through the
          same pipeline. The work happens in the background: this page just queues it, and each document
          shows its own progress on its deal's Documents tab. <strong>You can close this page</strong> once
          it has finished queueing.
        </div>

        {phase === 'idle' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {SCOPES.map(s => (
                <label key={s.key} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', cursor: 'pointer',
                  border: `1px solid ${scope === s.key ? B.navy : B.border}`, borderRadius: 8,
                  background: scope === s.key ? `${B.navy}0d` : B.surface,
                }}>
                  <input type="radio" name="intake-scope" checked={scope === s.key}
                    onChange={() => setScope(s.key)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: B.textLt }}>{s.hint}</div>
                  </div>
                </label>
              ))}
            </div>

            {loading ? <Spinner label="Counting documents…" />
              : err ? <ErrorMsg msg={err} />
              : targets && (
                <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
                  <strong>{targets.totalDeals}</strong> deal{targets.totalDeals === 1 ? '' : 's'} ·{' '}
                  <strong>{targets.totalDocuments}</strong> document{targets.totalDocuments === 1 ? '' : 's'}
                </div>
              )}

            {/* "Nothing to catch up" is a CLAIM ABOUT THE BOOK, so it may only
                be made when the count actually came back. A failed or pending
                read leaves `targets` null, and saying zero there tells the
                admin the work is done when nobody has counted it — which is
                exactly what the page did while both reads were timing out. */}
            <Btn onClick={run} disabled={loading || !!err || !targets?.totalDeals}>
              {targets?.totalDocuments
                ? `Queue ${targets.totalDocuments} document${targets.totalDocuments === 1 ? '' : 's'}`
                : loading ? 'Counting…'
                : err || !targets ? "Couldn't count documents"
                : 'Nothing to catch up'}
            </Btn>
          </>
        )}

        {phase !== 'idle' && (
          <>
            <div style={{ fontSize: 13, color: B.textMd, marginBottom: 8 }}>
              Queued {progress.done} / {progress.total} deals · {progress.filed.toLocaleString()} document{progress.filed === 1 ? '' : 's'} sent to the queue
              {progress.failed > 0 && <> · <span style={{ color: B.red }}>{progress.failed} deal{progress.failed === 1 ? '' : 's'} failed</span></>}
            </div>
            <div style={{ height: 8, borderRadius: 4, background: B.border, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: B.navy, transition: 'width .3s' }} />
            </div>
            <div style={{ fontSize: 11, color: B.textLt, marginBottom: 12 }}>
              This bar tracks QUEUEING only — the reading itself happens in the background afterwards.
              Watch "Reading progress" above for that, and leave this running until it reaches the last deal.
            </div>
            {phase === 'running'
              ? <Btn variant="ghost" size="sm" onClick={() => { stopRef.current = true; }}>Stop after current deals</Btn>
              : <Btn variant="secondary" size="sm" onClick={() => setPhase('idle')}>Done</Btn>}
          </>
        )}
      </Card>

      {log.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: B.text, marginBottom: 10 }}>Results</div>
          {log.map((row, i) => (
            <div key={`${row.dealName}-${i}`} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
              borderBottom: i === log.length - 1 ? 'none' : `1px solid ${B.divider}`, fontSize: 13,
            }}>
              <span style={{ flex: 1, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.dealName}
              </span>
              {row.error
                ? <Badge color={B.red}>{row.error}</Badge>
                : <>
                    {row.filed > 0 && <Badge color={B.green}>{row.filed} queued</Badge>}
                    {row.skipped > 0 && <Badge color={B.textLt}>{row.skipped} already done</Badge>}
                    {row.failures > 0 && <Badge color={B.red}>{row.failures} failed</Badge>}
                    {!row.filed && !row.skipped && !row.failures && <Badge color={B.textLt}>nothing to do</Badge>}
                  </>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};
